// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { DashboardPanel } from "types";
import { IAction, ActionType, raiseAction, IActionTypeSelectDashboardItem } from "actions";
import { isSet } from "utils";
import { getPanelSelection, toDropdownItems } from "guiutils";
import { IRegulation } from "parameter";
import { IState } from "states/states";

import Dropdown from "view/comp/Dropdown";

interface IControlCircuitSelectExtProps {
  className?: string;
  dashboardIndex?: number;
}

interface IControlCircuitSelectProps {
  controlCircuitIndex: number | undefined;
  controlParameters: IRegulation[];
  selectedDashboardIndex: number;
}

const mapStateToProps = (state: IState, ownProps: IControlCircuitSelectExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    DashboardPanel.MONITOR,
    ownProps.dashboardIndex,
  );
  let controlCircuitIndex = state.parameter?.regulation.findIndex(
    (curItem, curIndex) => (curItem.id ?? curIndex) === selection?.controlCircuitId,
  );
  if (isSet(controlCircuitIndex) && controlCircuitIndex < 0) {
    controlCircuitIndex = undefined;
  }
  if (!isSet(controlCircuitIndex) && state.parameter && state.parameter.regulation.length > 0) {
    controlCircuitIndex = 0; // make default selection
  }

  const props: IControlCircuitSelectProps = {
    controlCircuitIndex,
    controlParameters: state.parameter?.regulation ?? [],
    selectedDashboardIndex: index,
  };
  return props;
};

interface IControlCircuitSelectDispatch {
  onControlIdChanged: (index: number, controlId: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IControlCircuitSelectDispatch = {
    onControlIdChanged: (index: number, controlId: number) => {
      const payload: IActionTypeSelectDashboardItem = { index, selectedIds: [controlId] };
      dispatch(raiseAction(ActionType.ACT_SELECT_MONITOR, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_user_caption: {
    id: "Select control circuit",
  },
});

class ControlCircuitSelectComponent extends React.Component<
  IControlCircuitSelectExtProps &
    IControlCircuitSelectProps &
    IControlCircuitSelectDispatch &
    WrappedComponentProps
> {
  public render() {
    const circuitIndexes = Array.from(Array(this.props.controlParameters.length).keys()).sort(
      (a, b) =>
        this.props.controlParameters[a].name.localeCompare(this.props.controlParameters[b].name),
    );

    const controlcircuitItems = toDropdownItems(
      circuitIndexes,
      "controlId",
      (name, index) => {
        const id = this.props.controlParameters[index].id ?? index;
        this.props.onControlIdChanged(this.props.selectedDashboardIndex, id);
      },
      (index) => this.props.controlParameters[index]?.name,
    );

    return (
      <div id="controlcircuitselect" className={this.props.className}>
        <Dropdown
          items={controlcircuitItems}
          caption={this.props.intl.formatMessage(messages.msg_user_caption)}
          curItem={
            isSet(this.props.controlCircuitIndex)
              ? this.props.controlParameters[this.props.controlCircuitIndex]?.name
              : this.props.intl.formatMessage(messages.msg_user_caption)
          }
        />
      </div>
    );
  }
}

const ControlCircuitSelect = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ControlCircuitSelectComponent);

export default injectIntl(ControlCircuitSelect);
